import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { TranslateService } from '@ngx-translate/core';
import { filter, first, firstValueFrom, race, skip, Subject, take } from 'rxjs';
import { LANDING_URL, URL_CONSTANTS } from '../common/utils/url';
import { ConfirmationModalComponent } from '../standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { SpaceRepository } from '../state/space.repository';
import { ProviderStateService } from './provider-state.service';
import { SpaceRecordingService } from './space-recording.service';
import { UiService } from './ui.service';
import { UploadFileService } from './upload-file.service';
import { SessionSharedDataService } from './session-shared-data.service';

type ConfirmationOutput = 'confirm' | 'cancel';

/**
 * `force` skips the confirmation dialogs — for involuntary leaves (space deleted / access revoked)
 * and for the sidebar switcher, which is a deliberate one-click switch.
 */
interface LeaveOpts {
  force?: boolean;
}

/**
 * Owns every way of moving out of the current space: leaving to home and switching to another
 * space in place, together with the "Leave Space?" confirmation modal and the underlying router
 * navigation. Outer components only ever call {@link leaveToHome} or {@link switchToSpace}; the
 * gate (confirm + stop recording) and the navigation are private so the leave/switch semantics
 * live in exactly one place.
 *
 * A space switch reuses the already-mounted session infrastructure (socket, canvas, RTC) in place;
 * leaving to home lets the session component tear down on destroy (ngOnDestroy). Either way this
 * service owns only the gate and the navigation — per-space/UI teardown lives in the session
 * component.
 */
@Injectable({ providedIn: 'root' })
export class SpaceNavigationService {
  private readonly _leavingToHome$ = new Subject<void>();
  /**
   * Emits synchronously *before* navigating home when leaving the current space (voluntarily or
   * forced), not on a switch. Subscribers run their per-space teardown here so it completes while
   * the socket is still up — the socket owner (ContentComponent) is torn down after navigation.
   */
  readonly leavingToHome$ = this._leavingToHome$.asObservable();

  private ref?:
    | MatDialogRef<ConfirmationModalComponent>
    | MatBottomSheetRef<ConfirmationModalComponent>;

  /** The leave/switch currently running, if any. Resolves once its navigation has committed. */
  private inFlightNav?: Promise<boolean>;

  /**
   * At most one request waits behind the in-flight one, and a newer request replaces it — queueing
   * them all would walk the user through every space they clicked past, tearing each one down and
   * (in a call) confirming each hop.
   */
  private queuedNav?: {
    targetSpaceId?: string;
    opts?: LeaveOpts;
    resolve: (proceeded: boolean) => void;
    reject: (error: unknown) => void;
  };

  constructor(
    private router: Router,
    private matDialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private translate: TranslateService,
    private spaceRepo: SpaceRepository,
    private providerState: ProviderStateService,
    private spaceRecordingService: SpaceRecordingService,
    private uiService: UiService,
    private uploadFileService: UploadFileService,
    private sharedDataService: SessionSharedDataService,
    private zone: NgZone,
  ) {}

  /**
   * Leaves the current space entirely and navigates home. `opts.force` skips the confirmation
   * dialogs for involuntary leaves (space deleted / access revoked). Returns true when navigation
   * proceeds, false when it's a no-op or the user cancels.
   */
  leaveToHome(opts?: LeaveOpts): Promise<boolean> {
    return this.navigate(undefined, opts);
  }

  /**
   * Switches from the current space to another one (`targetSpaceId`) in place, reusing the mounted
   * session infrastructure. Returns true when navigation proceeds, false when it's a no-op (already
   * there) or the user cancels the "Leave Space?" confirmation.
   */
  switchToSpace(targetSpaceId: string, opts?: LeaveOpts): Promise<boolean> {
    return this.navigate(targetSpaceId, opts);
  }

  /**
   * Shows at most one confirmation before leaving: the "Leave Space?" modal when in a call,
   * otherwise the upload-in-progress warning. Resolves true to proceed, false if cancelled.
   */
  private async confirmLeave(): Promise<boolean> {
    if (this.providerState.callConnectedValue) {
      return (await this.openWarningModal()) === 'confirm';
    }
    return this.uploadFileService.confirmLeavingSpaceWithUploadInProgress();
  }

  /**
   * Stops any in-progress local recording before leaving, regardless of whether auto-local
   * recording is enabled. No-op (and no prompt) when nothing local is recording. When it does stop
   * one, the finished file is auto-downloaded so the user isn't prompted to save/upload on the way
   * out and doesn't silently lose their recording.
   */
  private async stopLocalRecordingIfNeeded(): Promise<void> {
    const hasActiveLocalRecording =
      this.spaceRecordingService.isRecordingInProgress() &&
      this.spaceRecordingService.isCurrentRecordingLocal();
    // Nothing to stop: return immediately so the switch (and its loader) isn't held up.
    if (!hasActiveLocalRecording) {
      return;
    }
    this.spaceRecordingService.flagAutoDownloadNextLocalRecording();
    await this.spaceRecordingService.stopRecording();
  }

  /**
   * Runs leave/switch flows one at a time. Concurrent ones would stack two confirmation modals,
   * tear the outgoing space down twice and race two router navigations — reachable by clicking two
   * spaces in the switcher in quick succession.
   */
  private navigate(targetSpaceId?: string, opts?: LeaveOpts): Promise<boolean> {
    if (!this.inFlightNav) {
      return this.startNavigation(targetSpaceId, opts);
    }

    // Only the newest request is worth running; whatever was waiting never started, so dropping it
    // costs nothing.
    this.queuedNav?.resolve(false);

    // Settled later — by runQueuedNavigation, or by a newer request replacing this one.
    return new Promise<boolean>((resolve, reject) => {
      this.queuedNav = { targetSpaceId, opts, resolve, reject };
    });
  }

  private async startNavigation(targetSpaceId?: string, opts?: LeaveOpts): Promise<boolean> {
    const navigation = this.runNavigation(targetSpaceId, opts);
    this.inFlightNav = navigation;

    try {
      return await navigation;
    } finally {
      this.inFlightNav = undefined;
      this.runQueuedNavigation();
    }
  }

  private async runQueuedNavigation(): Promise<void> {
    const queued = this.queuedNav;
    if (!queued) {
      return;
    }

    this.queuedNav = undefined;
    try {
      queued.resolve(await this.startNavigation(queued.targetSpaceId, queued.opts));
    } catch (error) {
      queued.reject(error);
    }
  }

  private async runNavigation(targetSpaceId?: string, opts?: LeaveOpts): Promise<boolean> {
    if (this.isAlreadyAtSpace(targetSpaceId)) {
      return false;
    }

    return this.performLeave(targetSpaceId, opts);
  }

  /**
   * True when the flow would be a no-op: it would reset the active space and emit sessionChanged
   * without changing routes. Evaluated when the navigation starts, so it also covers a queued
   * request for the space the previous one just reached — hence the URL check, since the router
   * ignores navigating to the URL it is already on and the space would sit blank behind its loader.
   */
  private isAlreadyAtSpace(targetSpaceId?: string): boolean {
    if (this.spaceRepo.activeSpaceId === targetSpaceId) {
      return true;
    }
    if (!targetSpaceId) {
      return false;
    }

    const routePath = new URL(this.router.url, window.location.origin).pathname;
    // Exact match: request-access / waiting-room carry the same id but are not the space itself.
    return routePath === `/${URL_CONSTANTS.SPACES}/${targetSpaceId}`;
  }

  private async performLeave(targetSpaceId?: string, opts?: LeaveOpts): Promise<boolean> {
    // Gate: forced leaves skip confirmation; voluntary ones show at most one modal. Either way,
    // stop any local recording before navigating.
    if (!opts?.force) {
      const confirmed = await this.confirmLeave();
      if (!confirmed) {
        return false;
      }
    }
    await this.stopLocalRecordingIfNeeded();

    // No target space → leaving the space entirely: go home and notify root-scoped listeners (e.g.
    // event-state-manager realtime cleanup). Per-space/UI teardown runs in the session component on
    // destroy (ngOnDestroy).
    if (!targetSpaceId) {
      await this.navigateHome();
      return true;
    }

    // Switching to another space: resolves once the router navigation settles (URL committed), so
    // the in-flight tracking releases the next queued switch only after the URL has caught up.
    await this.navigateToSpace(targetSpaceId);
    return true;
  }

  /**
   * Navigates to another space (or back to the spaces list when `id` is empty): shows the loader,
   * resets the active space and emits sessionChanged, then routes.
   */
  private navigateToSpace(id: string): Promise<boolean> {
    const oldSessionId = this.spaceRepo.activeSpace?._id;
    const oldBreakoutRoomId = this.spaceRepo.activeSpaceCurrentRoomUid;

    // Show the loader for the incoming space (also covers list→space, where no sessionChanged
    // handler runs).
    this.sharedDataService.dataLoading.next(true);

    // Emit sessionChanged so SessionComponent tears the outgoing space down (onSpaceSwitch →
    // teardownActiveSpace). The active-space reset lives at the end of teardownActiveSpace, so the
    // leader/follow + temp-metadata clears there run against the still-active space (resetting it
    // here would make them clear a null space and leave the old room's state lingering).
    this.sharedDataService.sessionChanged.next({
      oldSessionId: oldSessionId ?? undefined,
      newSessionId: id,
      oldBreakoutRoomId,
    });

    return this.router.navigate([`${URL_CONSTANTS.SPACES}/${id}`], {
      queryParamsHandling: 'preserve',
    });
  }

  /**
   * Navigates to Home. Used when leaving a space entirely. Per-space teardown is handled by the
   * session component's ngOnDestroy — this method only owns the navigation.
   */
  private navigateHome(): Promise<boolean> {
    this._leavingToHome$.next();
    return this.router.navigateByUrl(`/${LANDING_URL}`);
  }

  /**
   * Opens the confirmation inside the Angular zone. The leave/switch flows are reachable from
   * callbacks that have left it (Firebase auth, the call provider), and an overlay opened from
   * outside never gets the change-detection pass that renders its content and wires its backdrop —
   * it sits there half-drawn until something unrelated triggers CD. Running the open in the zone
   * forces that pass. Mirrors the same guard in ModalManagerService.showModal.
   */
  private async openWarningModal(): Promise<ConfirmationOutput> {
    try {
      return await this.zone.run(() => {
        const data = this.confirmationModalData;

        if (this.uiService.isMobile.getValue()) {
          const ref = this.bottomSheet.open(ConfirmationModalComponent, {
            data,
            panelClass: 'wb-exit-space-modal',
          });
          this.ref = ref;
          this.subscribeToCancelPopup();
          return firstValueFrom(ref.afterDismissed().pipe(take(1)));
        }

        const ref = this.matDialog.open(ConfirmationModalComponent, {
          data,
          panelClass: 'wb-exit-space-modal',
        });
        this.ref = ref;
        this.subscribeToCancelPopup();
        return firstValueFrom(ref.afterClosed().pipe(take(1)));
      });
    } finally {
      // Forget the closed modal so a later cancel can't act on it.
      this.ref = undefined;
    }
  }

  private get confirmationModalData() {
    return {
      title: this.translate.instant('Leave Space?'),
      description: this.translate.instant(
        'By leaving this Space, you will also leave your\ncall. Are you sure?',
      ),
      confirmButtonConfig: {
        icon: 'arrow_back',
        iconType: 'mat-icon',
        label: this.translate.instant('Leave Space'),
        danger: true,
      },
      cancelButtonConfig: {
        icon: 'close',
        iconType: 'mat-icon',
        label: this.translate.instant('Cancel'),
      },
    };
  }

  /** Auto-dismiss the confirmation if the underlying state changes (room switch / left / call ended). */
  private subscribeToCancelPopup(): void {
    race(
      this.spaceRepo.activeSpaceCurrentRoom$.pipe(skip(1)),
      this.spaceRepo.currentUserLeftSpace$,
      this.providerState.callConnected$.pipe(filter((value) => !value)),
    )
      .pipe(first())
      .subscribe(() => this.cancelConfirmation());
  }

  /** Closes the open confirmation as if the user had cancelled. No-op when none is open. */
  private cancelConfirmation(): void {
    if (!this.ref) {
      return;
    }
    if (this.ref instanceof MatBottomSheetRef) {
      this.ref.dismiss('cancel');
    } else {
      this.ref.close('cancel');
    }
  }
}
